import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Sparkles, Send } from 'lucide-react';
import { sendAdvisorMessage } from '../../lib/api';

const SUGGESTIONS = [
  'Compose a Surya Ritual (Awakening) for sensitive skin',
  'A Chandra Ritual (Sanctuary) for the close of the day',
  'How does Shreesavaa traceability actually work?',
];

const AIAdvisor = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        'Welcome. I am the Shreesavaa Skin & Ritual Advisor. Tell me what your skin is asking for today — and I will compose a ritual for you.',
    },
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: value }]);
    setLoading(true);
    try {
      const res = await sendAdvisorMessage({ session_id: sessionId, message: value });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            'A breath of static. I could not reach the well just now — please try again in a moment.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle orb */}
      <button
        data-testid="ai-advisor-toggle"
        aria-label="Open Skin Advisor"
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 group ${
          open ? 'scale-90' : 'scale-100'
        } transition-transform`}
      >
        <span
          className="absolute inset-0 rounded-full blur-xl opacity-60"
          style={{ background: '#AAC6D4' }}
          aria-hidden
        />
        <span
          className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full border border-[#90B0BF]/60 backdrop-blur-xl"
          style={{
            background:
              'radial-gradient(60% 60% at 30% 30%, #F4F8F9 0%, #C1D7E2 60%, #90B0BF 100%)',
          }}
        >
          {open ? (
            <X size={20} strokeWidth={1.5} className="text-[#7C9CB1]" />
          ) : (
            <Sparkles size={20} strokeWidth={1.5} className="text-[#7C9CB1]" />
          )}
        </span>
      </button>

      {/* Panel */}
      <div
        data-testid="ai-advisor-panel"
        className={`fixed z-50 bottom-24 right-4 left-4 md:left-auto md:right-8 md:w-[400px] origin-bottom-right transition-all duration-500 ${
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="rounded-2xl border border-[#C1D7E2] bg-[#F4F8F9]/90 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,156,177,0.4)] overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-5 pb-4 border-b border-[#C1D7E2]/70">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
              Shreesavaa
            </p>
            <h3 className="font-serif text-2xl text-[#7C9CB1] leading-snug">
              Skin &amp; Ritual Advisor
            </h3>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="ai-advisor-messages"
            className="thin-scroll px-5 py-5 max-h-[360px] overflow-y-auto space-y-4"
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  data-testid={`ai-msg-${m.role}-${idx}`}
                  className={`max-w-[85%] text-sm leading-relaxed font-light px-4 py-3 rounded-xl ${
                    m.role === 'user'
                      ? 'bg-[#7C9CB1] text-[#F4F8F9] rounded-br-sm'
                      : 'bg-[#C1D7E2]/60 text-[#7C9CB1] rounded-bl-sm border border-[#C1D7E2]'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="ai-advisor-loading">
                <div className="px-4 py-3 rounded-xl rounded-bl-sm bg-[#C1D7E2]/60 border border-[#C1D7E2]">
                  <span className="inline-flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-[#7C9CB1] animate-pulse"
                        style={{ animationDelay: `${i * 150}ms` }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  data-testid={`ai-suggestion-${i}`}
                  className="text-[11px] tracking-wide font-light px-3 py-1.5 rounded-full border border-[#90B0BF]/60 text-[#7C9CB1] hover:bg-[#C1D7E2]/40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="px-4 py-4 border-t border-[#C1D7E2]/70 flex items-center gap-2"
          >
            <input
              data-testid="ai-advisor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the advisor…"
              className="flex-1 bg-transparent text-sm text-[#7C9CB1] placeholder-[#90B0BF]/60 focus:outline-none px-2 py-2 font-light"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="ai-advisor-send"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#7C9CB1] text-[#F4F8F9] disabled:opacity-40 hover:bg-[#90B0BF] transition-colors"
            >
              <Send size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AIAdvisor;
